/**
 * White-label theme defaults.
 *
 * Brand name / logo / title are overridden by `APP_*` env vars
 * (`src/config/appEnv.ts`). Colors, fonts, and buttons stay here.
 *
 * Tokens follow `antiques_design/tokens.css` (Figma Antique Experts).
 * Applied at startup by `applyTheme()` in `main.tsx`.
 */

import { appEnv } from "@/config/appEnv";

const defaultThemeConfig = {
  brand: {
    name: "Expert",
    appTitle: "Expert Portal",
    logoSrc: "/antique-logo.png",
    logoAlt: "Expert",
    faviconSrc: "/favicon.png",
    reportName: "Expert",
    portalLabels: {
      expert: "Expert Portal",
      client: "Client Portal",
    },
    panelSubtitle: "Expert panel",
  },

  fonts: {
    /** CSS font-family for body UI (Figma Body = Satoshi) */
    sans: '"Satoshi", "Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
    /** Display font for page titles (Figma Title = Cinzel) */
    display: '"Cinzel", ui-serif, Georgia, serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    /** Google Fonts stylesheet — Cinzel + Plus Jakarta fallback */
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap",
    /** Fontshare Satoshi (Figma body face) */
    extraFontsUrl:
      "https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap",
  },

  colors: {
    canvas: "#fff8f0",
    surface: "#ffffff",

    primary: "#8f6336",
    primaryHover: "#78542e",
    primaryActive: "#674726",
    primarySoft: "#f2e1c4",

    secondary: "#ea580c",
    secondaryHover: "#c2410c",
    secondaryActive: "#9a3412",
    secondarySoft: "#fff7ed",

    text: "#131220",
    textMuted: "#5d5d5d",
    border: "#e7e7e7",
    inputBg: "#ffffff",
    inputBorder: "#e7e7e7",

    logoBg: "#f2e1c4",
    logoAccent: "#8f6336",
    successRing: "#ebfff2",
    successCheck: "#22c55e",

    expertSidebar: "#8f6336",
    expertSidebarForeground: "#ffffff",
    expertSidebarMuted: "rgba(255, 255, 255, 0.65)",
    expertDashboardCanvas: "#f6f6f6",
    expertNavBadge: "#483016",
    expertAvailable: "#22c55e",
    expertStatusActive: "#22c55e",
    expertStatusInactive: "#eab308",

    expertActionGreen: "#16a34a",
    expertActionGreenHover: "#15803d",
    expertActionGreenSoft: "#ebfff2",
    expertActionGreenText: "#166534",
    expertActionGreenRing: "rgba(22, 163, 74, 0.22)",

    expertActionBlue: "#3b82f6",
    expertActionBlueHover: "#2563eb",
    expertActionBlueSoft: "#eff6ff",
    expertActionBlueText: "#1d4ed8",
    expertActionBlueRing: "rgba(59, 130, 246, 0.25)",

    expertActionAmber: "#eab308",
    expertActionAmberHover: "#ca8a04",
    expertActionAmberSoft: "#fef3c7",
    expertActionAmberText: "#92400e",
    expertActionAmberRing: "rgba(234, 179, 8, 0.45)",

    expertActionOrange: "#ea580c",
    expertActionOrangeHover: "#c2410c",

    expertBadgeNeutral: "#fff7ed",
    expertBadgeNeutralText: "#ea580c",
    expertBadgeNeutralRing: "rgba(234, 88, 12, 0.25)",

    expertStatusDraftBg: "#e7e7e7",
    expertStatusDraftText: "#5d5d5d",
    expertStatusNewBg: "#fef9c3",
    expertStatusNewText: "#a16207",
    expertStatusDoneBg: "#ebfff2",
    expertStatusDoneText: "#15803d",
    expertStatusExpiredBg: "#fff7ed",
    expertStatusExpiredText: "#c2410c",

    expertError: "#ef4444",
    expertErrorSoft: "#fee2e2",

    expertDraftBanner: "#ebfff2",
    expertDraftBannerBorder: "#bbf7d0",
    expertDraftBannerText: "#166534",
  },

  buttons: {
    sizes: {
      sm: {
        height: "1.75rem",
        paddingX: "1.75rem",
        fontSize: "0.75rem",
        fontWeight: "500",
        radius: "0.5rem",
        minWidth: "",
      },
      md: {
        height: "2.5rem",
        paddingX: "1rem",
        fontSize: "0.875rem",
        fontWeight: "500",
        radius: "0.5rem",
        minWidth: "8.5rem",
      },
      lg: {
        height: "auto",
        paddingX: "1rem",
        paddingY: "0.75rem",
        fontSize: "1rem",
        fontWeight: "500",
        radius: "0.5rem",
        minWidth: "",
      },
    },
    variants: {
      primary: {
        bg: "bg-primary",
        hover: "hover:bg-primary-hover",
        active: "active:bg-primary-active",
        text: "text-white",
        border: "",
      },
      secondary: {
        bg: "bg-secondary",
        hover: "hover:bg-secondary-hover",
        active: "active:bg-secondary-active",
        text: "text-white",
        border: "",
      },
      outline: {
        bg: "bg-white",
        hover: "hover:bg-primary/[0.04]",
        active: "",
        text: "text-primary",
        border: "border border-primary",
      },
      ghost: {
        bg: "bg-transparent",
        hover: "hover:bg-primary-soft",
        active: "",
        text: "text-primary",
        border: "",
      },
      success: {
        bg: "bg-expert-action-green",
        hover: "hover:bg-expert-action-green-hover",
        active: "",
        text: "text-white",
        border: "",
      },
      info: {
        bg: "bg-expert-action-blue",
        hover: "hover:bg-expert-action-blue-hover",
        active: "",
        text: "text-white",
        border: "",
      },
    },
  },

  icons: {
    nav: {
      sizePx: 18,
      strokeWidth: 1.75,
      queueSrc: "/expert-nav-queue-icon.png",
    },
    logo: {
      sizePx: 40,
      radius: "0.5rem",
    },
  },

  /** Evaluation / PDF report copy (brand colors still come from `colors`) */
  report: {
    subtitle: "Expert Evaluation",
    title: "Evaluation Report",
    layoutVersion: "v1" as "v1" | "v2",
  },
} as const;

export const themeConfig = {
  ...defaultThemeConfig,
  brand: {
    ...defaultThemeConfig.brand,
    name: appEnv.name || defaultThemeConfig.brand.name,
    appTitle: appEnv.title || defaultThemeConfig.brand.appTitle,
    logoSrc: appEnv.logoUrl || defaultThemeConfig.brand.logoSrc,
    logoAlt: appEnv.name || defaultThemeConfig.brand.logoAlt,
    faviconSrc: appEnv.faviconUrl || defaultThemeConfig.brand.faviconSrc,
    reportName: appEnv.reportName || defaultThemeConfig.brand.reportName,
  },
};

export type ThemeConfig = typeof defaultThemeConfig;
export type ButtonSize = keyof typeof defaultThemeConfig.buttons.sizes;
export type ButtonVariant = keyof typeof defaultThemeConfig.buttons.variants;
